import { createQueryString } from '~/domains/common/utils/queryString';
import type { BonusesResponse, GetBonusesParams } from '~/domains/promotions/types/bonus.types';

import { bonusesServiceInstance } from './bonuses.service.instance';

/**
 * Get bonuses from the API
 * @param params - Optional pagination and filter parameters
 * @returns Promise<BonusesResponse> Paginated bonus definitions
 *
 * @example
 * ```typescript
 * // Get all bonuses
 * const bonuses = await getBonuses();
 *
 * // Get bonuses for first deposit trigger
 * const firstDepositBonuses = await getBonuses({
 *   trigger: 'FIRST_DEPOSIT',
 *   page: 1,
 *   pageSize: 10,
 * });
 *
 * // Get bonuses for registration trigger
 * const registrationBonuses = await getBonuses({
 *   trigger: 'REGISTRATION',
 *   page: 1,
 *   pageSize: 10,
 * });
 * ```
 */
export const getBonuses = async (params?: GetBonusesParams): Promise<BonusesResponse> => {
  const queryParams = createQueryString((params ?? {}) as Parameters<typeof createQueryString>[0]);

  const url = queryParams || '/';
  const response = await bonusesServiceInstance.get<BonusesResponse>(url);
  return response.data;
};

/**
 * Get assigned bonuses for the authenticated user from the API
 * @param params - Optional pagination and filter parameters
 * @returns Promise<BonusesResponse> Paginated bonus definitions assigned to the user
 *
 * @example
 * ```typescript
 * // Get all assigned bonuses for the current user
 * const assignedBonuses = await getAssignedBonuses();
 *
 * // Get assigned bonuses with pagination
 * const paginatedBonuses = await getAssignedBonuses({
 *   page: 1,
 *   pageSize: 10,
 * });
 *
 * // Get assigned bonuses for first deposit trigger
 * const firstDepositBonuses = await getAssignedBonuses({
 *   trigger: 'FIRST_DEPOSIT',
 *   page: 1,
 *   pageSize: 10,
 * });
 * ```
 */
export const getAssignedBonuses = async (params?: GetBonusesParams): Promise<BonusesResponse> => {
  const queryParams = createQueryString((params ?? {}) as Parameters<typeof createQueryString>[0]);

  const url = `/assigned${queryParams}`;
  const response = await bonusesServiceInstance.get<BonusesResponse>(url);
  return response.data;
};
